import { useEffect, useRef, useState, type FormEvent } from "react";
import { LoaderCircle, X } from "lucide-react";
import type { DriverDescriptor, DriverId, ProjectSummary } from "../types";

interface CreateSessionDialogProps {
  open: boolean;
  busy: boolean;
  error: string;
  workspaceRoot: string;
  drivers: DriverDescriptor[];
  projects: ProjectSummary[];
  initialProjectId: string | null;
  onClose(): void;
  onCreate(input: { title: string; projectId: string | null; workspacePath: string; driverId: DriverId }): Promise<void>;
}

export function CreateSessionDialog(props: CreateSessionDialogProps) {
  const [title, setTitle] = useState("");
  const [workspacePath, setWorkspacePath] = useState(props.workspaceRoot);
  const [driverId, setDriverId] = useState<DriverId>("codex");
  const [projectId, setProjectId] = useState("");
  const wasOpen = useRef(false);

  useEffect(() => {
    if (props.open && !wasOpen.current) {
      setTitle("");
      const project = props.projects.find((candidate) => candidate.id === props.initialProjectId);
      setProjectId(project?.id ?? "");
      setWorkspacePath(project?.workspacePath ?? props.workspaceRoot);
      setDriverId("codex");
    }
    wasOpen.current = props.open;
  }, [props.open]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (props.busy) return;
    await props.onCreate({ title, projectId: projectId || null, workspacePath, driverId });
  }

  if (!props.open) return null;
  return (
    <div className="dialog-layer" role="presentation">
      <button className="dialog-backdrop" type="button" onClick={props.onClose} disabled={props.busy} aria-label="Fermer" />
      <section className="create-dialog" role="dialog" aria-modal="true" aria-busy={props.busy} aria-labelledby="create-session-title">
        <header>
          <h2 id="create-session-title">Nouvelle session</h2>
          <button className="icon-command" type="button" onClick={props.onClose} disabled={props.busy} aria-label="Fermer"><X aria-hidden="true" /></button>
        </header>
        <form onSubmit={submit}>
          <label className="form-field">
            <span>Nom (facultatif)</span>
            <input aria-label="Nom" value={title} onChange={(event) => setTitle(event.target.value)} maxLength={120} placeholder="Nouvelle conversation" autoFocus />
          </label>
          <label className="form-field">
            <span>Projet</span>
            <select
              aria-label="Projet"
              value={projectId}
              onChange={(event) => {
                const nextId = event.target.value;
                const project = props.projects.find((candidate) => candidate.id === nextId);
                setProjectId(nextId);
                setWorkspacePath(project?.workspacePath ?? props.workspaceRoot);
              }}
            >
              <option value="">Sans projet</option>
              {props.projects.map((project) => <option value={project.id} key={project.id}>{project.name}</option>)}
            </select>
          </label>
          <label className="form-field">
            <span>Driver</span>
            <select aria-label="Driver" value={driverId} onChange={(event) => setDriverId(event.target.value as DriverId)}>
              {props.drivers.map((driver) => <option value={driver.id} key={driver.id}>{driver.name}</option>)}
            </select>
            <small>{props.drivers.find((driver) => driver.id === driverId)?.description}</small>
          </label>
          <label className="form-field">
            <span>Workspace</span>
            <input aria-label="Workspace" value={workspacePath} maxLength={2000} readOnly />
            <small>{projectId ? "Le dossier est défini par le projet." : "Espace général isolé."}</small>
          </label>
          {props.error ? <p className="form-error" role="alert">{props.error}</p> : null}
          <footer>
            <button className="text-command" type="button" onClick={props.onClose} disabled={props.busy}>Annuler</button>
            <button className="text-command primary" type="submit" disabled={props.busy || !workspacePath.trim()}>{props.busy ? <LoaderCircle className="spin" aria-hidden="true" /> : null}{props.busy ? "Création…" : "Créer"}</button>
          </footer>
        </form>
      </section>
    </div>
  );
}
